import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { BreadcrumbContext } from '../components/Breadcrumb';
import { BreadcrumbService } from '../services/breadcrumbService';

/**
 * Custom hook for automatic breadcrumb generation
 * Follows service-oriented architecture pattern
 * 
 * @param {object} params - Context parameters for breadcrumb generation
 * @param {string} params.organizationId - Current organization ID
 * @param {string} params.accountId - Current account ID  
 * @param {string} params.siteId - Current site ID
 * @param {string} params.siteName - Display name for current site
 * @param {string} params.collectionName - Display name for current collection/account
 * @param {string} params.accountName - Account name (fallback for collectionName)
 * @param {string} params.organizationName - Organization name
 * @param {Array} params.customBreadcrumbs - Optional: override automatic generation
 * @param {boolean} params.disabled - Optional: disable automatic breadcrumb generation
 */
export const useBreadcrumbs = (params = {}) => {
  const location = useLocation();
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  
  const {
    organizationId,
    accountId,
    siteId,
    siteName,
    collectionName,
    accountName,
    organizationName,
    customBreadcrumbs,
    disabled = false
  } = params;

  useEffect(() => {
    // Skip if disabled or no setBreadcrumb function
    if (disabled || !setBreadcrumb) {
      return;
    }

    // Use custom breadcrumbs if provided
    if (customBreadcrumbs && Array.isArray(customBreadcrumbs)) {
      if (BreadcrumbService.validateBreadcrumbs(customBreadcrumbs)) {
        setBreadcrumb(customBreadcrumbs);
      } else {
        console.warn('Invalid custom breadcrumbs provided:', customBreadcrumbs);
      }
      return;
    }

    // Generate breadcrumbs automatically
    const breadcrumbs = BreadcrumbService.generateBreadcrumbs(location.pathname, {
      organizationId,
      accountId,
      siteId,
      siteName,
      collectionName: collectionName || accountName,
      accountName,
      organizationName
    });

    // Validate and set breadcrumbs
    if (BreadcrumbService.validateBreadcrumbs(breadcrumbs)) {
      setBreadcrumb(breadcrumbs);
    } else {
      console.warn('Generated invalid breadcrumbs for path:', location.pathname, breadcrumbs);
      // Fallback to basic breadcrumb
      setBreadcrumb([
        { to: "/", text: "Home", active: false },
        { to: null, text: "Page", active: true }
      ]);
    }
  }, [
    location.pathname,
    organizationId,
    accountId,
    siteId,
    siteName,
    collectionName,
    accountName,
    organizationName,
    customBreadcrumbs,
    disabled,
    setBreadcrumb
  ]);

  // Return route parameters for convenience
  return {
    routeParams: BreadcrumbService.extractRouteParams(location.pathname),
    currentPath: location.pathname
  };
};

/**
 * Simplified hook for basic breadcrumb usage
 * @param {string} title - Title for the current page
 * @param {string} parentPath - Optional parent path
 * @param {string} parentTitle - Optional parent title
 */
export const useSimpleBreadcrumbs = (title, parentPath = null, parentTitle = null) => {
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  
  useEffect(() => {
    if (!setBreadcrumb) return;

    const breadcrumbs = [
      { to: "/", text: "Home", active: false }
    ];

    if (parentPath && parentTitle) {
      breadcrumbs.push({ to: parentPath, text: parentTitle, active: false });
    }

    breadcrumbs.push({ to: null, text: title, active: true });

    setBreadcrumb(breadcrumbs);
  }, [title, parentPath, parentTitle, setBreadcrumb]);
};

export default useBreadcrumbs;